import { UserProfile, ProfileField, ProfileCategory, ProfileStrengthData } from '@/types/profile';
import { getProfileCategories } from '@/config/profile-fields';

/**
 * Create profile strength data using backend-calculated strength as source of truth
 * This provides UI-specific data (categories, next field) while using backend's precise calculation
 */
export function createProfileStrengthData(
  profile: UserProfile, 
  userType: 'student' | 'teacher'
): ProfileStrengthData {
  const categories = getProfileCategories(userType);
  const categoryStats: ProfileStrengthData['categories'] = {};
  const completedFields: string[] = [];
  const pendingFields: ProfileField[] = [];

  categories.forEach(category => {
    let completedWeight = 0;
    let totalWeight = 0;
    let completedCount = 0;
    let totalCount = category.fields.length;

    category.fields.forEach(field => {
      totalWeight += field.weight;
      
      const value = getNestedValue(profile, field.key);
      const isCompleted = isFieldCompleted(value, field.type);
      
      if (isCompleted) {
        completedWeight += field.weight;
        completedCount += 1;
        completedFields.push(field.key);
      } else {
        pendingFields.push(field);
      }
    });

    // Calculate category strength as percentage
    const categoryStrength = totalWeight > 0 ? (completedWeight / totalWeight) * 100 : 0;

    categoryStats[category.key] = {
      completed: completedCount,
      total: totalCount,
      weight: category.weight,
      strength: categoryStrength
    };
  });

  // Use backend's profile strength as source of truth (trimmed to integer for consistency)
  const backendStrength = profile.profile_strength || 0;
  const totalStrength = Math.floor(backendStrength);

  // Find next recommended field (highest weight field that's not completed)
  const nextRecommendedField = findNextRecommendedField(pendingFields);

  return {
    totalStrength,
    categories: categoryStats,
    nextRecommendedField,
    completedFields,
    pendingFields
  };
}

/**
 * @deprecated Use createProfileStrengthData instead - this function recalculates what backend already provides
 * Legacy function kept for backward compatibility during migration
 */
export function calculateProfileStrength(
  profile: UserProfile, 
  userType: 'student' | 'teacher'
): ProfileStrengthData {
  return createProfileStrengthData(profile, userType);
}

/**
 * Get nested value from object using dot notation
 */
function getNestedValue(obj: any, path: string): any {
  return path.split('.').reduce((current, key) => {
    return current && current[key] !== undefined ? current[key] : undefined;
  }, obj);
}

/**
 * Check if a field value is considered complete based on its type
 */
function isFieldCompleted(value: any, fieldType: ProfileField['type']): boolean {
  if (value === null || value === undefined) return false;
  
  switch (fieldType) {
    case 'text':
    case 'email':
    case 'tel':
    case 'textarea':
    case 'date':
    case 'select':
      return typeof value === 'string' && value.trim().length > 0;
    
    case 'number':
      return typeof value === 'number' && value >= 0;
    
    case 'multiselect':
      return Array.isArray(value) && value.length > 0;
    
    case 'avatar':
      return typeof value === 'string' && value.trim().length > 0;
    
    default:
      return false;
  }
}

/**
 * Find the next recommended field to complete (highest weight incomplete field)
 */
function findNextRecommendedField(pendingFields: ProfileField[]): ProfileField | undefined {
  if (pendingFields.length === 0) return undefined;
  
  // Sort by weight (descending) and then by required status
  const sortedFields = pendingFields.sort((a, b) => {
    if (a.required && !b.required) return -1;
    if (!a.required && b.required) return 1;
    return b.weight - a.weight;
  });
  
  return sortedFields[0];
}

/**
 * Get completion percentage for a specific category
 */
export function getCategoryCompletion(
  profile: UserProfile,
  category: ProfileCategory
): { completed: number; total: number; percentage: number } {
  let completed = 0;
  const total = category.fields.length;
  
  category.fields.forEach(field => {
    const value = getNestedValue(profile, field.key);
    if (isFieldCompleted(value, field.type)) {
      completed++;
    }
  });
  
  const percentage = total > 0 ? (completed / total) * 100 : 0;
  
  return { completed, total, percentage: Math.floor(percentage) };
}

/**
 * Get profile strength level and description
 */
export function getProfileStrengthLevel(strength: number): {
  level: 'beginner' | 'intermediate' | 'advanced' | 'complete';
  title: string;
  description: string;
  color: string;
} {
  if (strength >= 90) {
    return {
      level: 'complete',
      title: 'Profile Complete',
      description: 'Your profile is comprehensive and detailed!',
      color: 'text-green-600'
    };
  } else if (strength >= 70) {
    return {
      level: 'advanced',
      title: 'Strong Profile',
      description: 'Your profile is well-developed with most details filled.',
      color: 'text-green-600'
    };
  } else if (strength >= 30) {
    return {
      level: 'intermediate',
      title: 'Good Start',
      description: 'You\'re making good progress on your profile.',
      color: 'text-orange-600'
    };
  } else {
    return {
      level: 'beginner',
      title: 'Getting Started',
      description: 'Complete your profile to unlock more features.',
      color: 'text-red-600'
    };
  }
}

/**
 * Get recommended actions based on profile strength
 */
export function getRecommendedActions(
  strengthData: ProfileStrengthData,
  userType: 'student' | 'teacher'
): Array<{
  action: string;
  description: string;
  priority: 'high' | 'medium' | 'low';
  field?: ProfileField;
}> {
  const actions: Array<{
    action: string;
    description: string;
    priority: 'high' | 'medium' | 'low';
    field?: ProfileField;
  }> = [];

  // High priority: Complete required fields
  const requiredPendingFields = strengthData.pendingFields.filter(field => field.required);
  requiredPendingFields.forEach(field => {
    actions.push({
      action: `Complete ${field.label}`,
      description: `This is a required field that helps others understand your background.`,
      priority: 'high',
      field
    });
  });

  // Medium priority: Complete basic info if not done
  const basicInfoStrength = strengthData.categories['basic_info']?.strength || 0;
  if (basicInfoStrength < 80) {
    actions.push({
      action: 'Complete Basic Information',
      description: 'Add essential details like your name and contact information.',
      priority: 'medium'
    });
  }

  // Medium priority: Complete academic/professional info
  const academicKey = userType === 'student' ? 'academic_info' : 'professional_info';
  const academicStrength = strengthData.categories[academicKey]?.strength || 0;
  if (academicStrength < 70) {
    const section = userType === 'student' ? 'academic' : 'professional';
    actions.push({
      action: `Complete ${section} Information`,
      description: `Share your ${section} background to get better recommendations.`,
      priority: 'medium'
    });
  }

  // Low priority: Add optional fields for personalization
  if (strengthData.totalStrength > 60) {
    const personalStrength = strengthData.categories['personal_info']?.strength || 0;
    if (personalStrength < 50) {
      actions.push({
        action: 'Add Personal Details',
        description: 'Personalize your profile with a photo and bio.',
        priority: 'low'
      });
    }
  }

  return actions.slice(0, 4); // Return top 4 actions
}

/**
 * Generate profile completion tips
 */
export function getProfileCompletionTips(userType: 'student' | 'teacher'): string[] {
  const commonTips = [
    'Complete your basic information first - it\'s the foundation of your profile.',
    'Add a profile picture to make your profile more personal and trustworthy.',
    'Write a brief bio to help others understand your background and interests.'
  ];

  const studentTips = [
    'Share your academic interests to get personalized learning recommendations.',
    'Add your learning goals to track your educational progress.',
    'List your achievements and extracurricular activities to showcase your talents.'
  ];

  const teacherTips = [
    'List your qualifications and certifications to build credibility.',
    'Share your teaching experience and subjects to attract the right students.',
    'Describe your teaching style to help students understand your approach.'
  ];

  return [...commonTips, ...(userType === 'student' ? studentTips : teacherTips)];
}
